import { api } from './api'
import { LoginRequest, RegisterRequest, AuthResponse, User } from '@/types'

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/login', credentials)
      return response.data.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async adminLogin(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/admin-login', credentials)
      return response.data.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async staffLogin(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/staff-login', credentials)
      return response.data.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/register', userData)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/refresh-token', { refreshToken })
      
      if (response.data.success && response.data.data) {
        return response.data.data
      }
      
      throw new Error('Invalid refresh token response')
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Don't throw error for logout, just log it
      console.error('Logout error:', error)
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await api.post('/auth/forgot-password', { email })
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async resetPassword(email: string, otp: string, password: string): Promise<void> {
    try {
      await api.post('/auth/reset-password', { email, otp, password })
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword })
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async verifyEmail(token: string): Promise<void> {
    try {
      await api.get(`/auth/verify-email/${token}`)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get<{ data: User }>('/auth/me')
      return response.data.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  private handleError(error: any): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message)
    }
    
    if (error.message) {
      return new Error(error.message)
    }
    
    return new Error('An unexpected error occurred')
  }
}

export const authService = new AuthService()
